import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SortableHeader, sortData } from '../components/SortableHeader';
import { ExportButtons } from '../components/ExportButtons';
import { 
    InvoicesFilters,
    InvoicesTable,
    InvoicesSummary,
    InvoiceDetailModal,
    ManualVerifyModal
} from '../components/invoices';
import { toast } from 'sonner';
import { toastDeletedWithUndo } from '../lib/undoDelete';
import { ReassignUserDialog } from '../components/admin/ReassignUserDialog';
import { FileText, Mail, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const API = `/api`;

export default function Invoices() {
    const { user, getAuthHeaders } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [reassign, setReassign] = useState(null);
    const [confirmingId, setConfirmingId] = useState(null);
    const [verifyingAll, setVerifyingAll] = useState(false);
    
    // Manual verification modal states
    const [manualVerifyModal, setManualVerifyModal] = useState(false);
    const [manualVerifyInvoice, setManualVerifyInvoice] = useState(null);
    const [manualVerifyNote, setManualVerifyNote] = useState('');
    const [manualVerifyBank, setManualVerifyBank] = useState('');
    const [manualVerifySender, setManualVerifySender] = useState('');
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('all');
    const [users, setUsers] = useState([]);
    
    // Sorting states
    const [sortKey, setSortKey] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    
    // Pagination state
    const [visibleCount, setVisibleCount] = useState(10);
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        fetchInvoices();
        if (user?.role === 'admin' || user?.role === 'supervisor') {
            fetchUsers();
        }
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/users`, {
                headers: getAuthHeaders()
            });
            setUsers(response.data.filter(u => u.role === 'usuario'));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const PAGE = 200;
    const [summary, setSummary] = useState(null);
    const [lastFilters, setLastFilters] = useState({});
    const [loadingMore, setLoadingMore] = useState(false);

    const buildParams = (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.dateFrom) params.append('date_from', filters.dateFrom);
        if (filters.dateTo) params.append('date_to', filters.dateTo);
        if (filters.userId) params.append('user_id', filters.userId);
        return params;
    };

    const fetchInvoices = async (filters = {}) => {
        setLoading(true);
        try {
            setLastFilters(filters);
            const params = buildParams(filters);
            params.append('limit', String(PAGE));
            const [listRes, sumRes] = await Promise.all([
                axios.get(`${API}/invoices?${params.toString()}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/invoices/summary?${buildParams(filters).toString()}`, { headers: getAuthHeaders() }).catch(() => null),
            ]);
            setInvoices(listRes.data);
            setSummary(sumRes?.data || null);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toast.error('Error al cargar las facturas');
        } finally {
            setLoading(false);
        }
    };

    // Fetch the next server page when the user asked for more than what is loaded
    const fetchMoreFromServer = async () => {
        setLoadingMore(true);
        try {
            const params = buildParams(lastFilters);
            params.append('limit', String(PAGE));
            params.append('skip', String(invoices.length));
            const res = await axios.get(`${API}/invoices?${params.toString()}`, { headers: getAuthHeaders() });
            const seen = new Set(invoices.map(i => i.id));
            setInvoices([...invoices, ...res.data.filter(i => !seen.has(i.id))]);
        } catch (error) {
            toast.error('Error al cargar más facturas');
        } finally {
            setLoadingMore(false);
        }
    };

    const handleSearch = () => {
        const filters = {};
        if (searchTerm) filters.search = searchTerm;
        if (dateFrom) filters.dateFrom = format(dateFrom, 'yyyy-MM-dd');
        if (dateTo) filters.dateTo = format(dateTo, 'yyyy-MM-dd');
        if (selectedUserId && selectedUserId !== 'all') filters.userId = selectedUserId;
        fetchInvoices(filters);
    };

    const selectRange = (from, to) => {
        setDateFrom(from);
        setDateTo(to);
        setVisibleCount(10);
        const filters = { dateFrom: format(from, 'yyyy-MM-dd'), dateTo: format(to, 'yyyy-MM-dd') };
        if (searchTerm) filters.search = searchTerm;
        if (selectedUserId && selectedUserId !== 'all') filters.userId = selectedUserId;
        fetchInvoices(filters);
    };
    const selectSingleDay = (day) => selectRange(day, day);

    const clearFilters = () => {
        setSearchTerm('');
        setDateFrom(null);
        setDateTo(null);
        setSelectedUserId('all');
        setVisibleCount(10);
        fetchInvoices();
    };

    const hasActiveFilters = searchTerm || dateFrom || dateTo || (selectedUserId && selectedUserId !== 'all');
    const canFilterByUser = user?.role === 'admin' || user?.role === 'supervisor';
    
    const handleSort = (key, direction) => {
        setSortKey(key);
        setSortDirection(direction);
    };
    
    const sortedInvoices = useMemo(() => {
        return sortData(invoices, sortKey, sortDirection);
    }, [invoices, sortKey, sortDirection]);
    
    const paginatedInvoices = useMemo(() => {
        return sortedInvoices.slice(0, visibleCount);
    }, [sortedInvoices, visibleCount]);
    
    const totalCount = summary ? summary.count : sortedInvoices.length;
    const hasMoreInvoices = sortedInvoices.length > visibleCount || invoices.length < totalCount;
    
    const loadMore = () => {
        if (visibleCount + ITEMS_PER_PAGE > invoices.length && invoices.length < totalCount && !loadingMore) {
            fetchMoreFromServer();
        }
        setVisibleCount(prev => prev + ITEMS_PER_PAGE);
    };

    const handleConfirm = async (invoiceId, currentStatus) => {
        setConfirmingId(invoiceId);
        try {
            const response = await axios.patch(
                `${API}/invoices/${invoiceId}/confirm`,
                {},
                { headers: getAuthHeaders() }
            );
            
            setInvoices(invoices.map(inv => 
                inv.id === invoiceId 
                    ? { ...inv, confirmed: response.data.confirmed }
                    : inv
            ));
            
            toast.success(response.data.confirmed ? 'Factura confirmada' : 'Confirmación removida');
        } catch (error) {
            console.error('Error confirming invoice:', error);
            toast.error('Error al confirmar la factura');
        } finally {
            setConfirmingId(null);
        }
    };

    const handleVerifyZelle = async (invoiceId) => {
        setConfirmingId(invoiceId);
        try {
            const response = await axios.post(
                `${API}/invoices/${invoiceId}/verify-zelle`,
                {},
                { headers: getAuthHeaders() }
            );
            
            if (response.data.verified) {
                setInvoices(invoices.map(inv => 
                    inv.id === invoiceId 
                        ? { ...inv, confirmed: true, auto_verified: true }
                        : inv
                ));
                toast.success(`✅ ${response.data.message} - ${response.data.match?.bank || ''}`);
            } else {
                toast.error(`❌ ${response.data.message}`);
            }
        } catch (error) {
            console.error('Error verifying invoice:', error);
            toast.error('Error al verificar con Zelle');
        } finally {
            setConfirmingId(null);
        }
    };

    // Re-verify a pending invoice against email cache
    const handleReVerify = async (invoiceId) => {
        setConfirmingId(invoiceId);
        try {
            const response = await axios.post(
                `${API}/invoices/${invoiceId}/re-verify`,
                {},
                { headers: getAuthHeaders() }
            );
            
            if (response.data.verified) {
                setInvoices(invoices.map(inv => 
                    inv.id === invoiceId 
                        ? { ...inv, confirmed: true, auto_verified: true }
                        : inv
                ));
                toast.success(`✅ ${response.data.message}`);
            } else if (response.data.needs_review) {
                setInvoices(invoices.map(inv => 
                    inv.id === invoiceId 
                        ? { ...inv, needs_review: true }
                        : inv
                ));
                toast.warning(`⚠️ ${response.data.message}`);
            } else {
                toast.error(`❌ ${response.data.message}`);
            }
        } catch (error) {
            console.error('Error re-verifying invoice:', error);
            toast.error('Error al reenviar verificación');
        } finally {
            setConfirmingId(null);
        }
    };

    // Verify all pending invoices against emails
    const handleVerifyAllPending = async () => {
        const pendingCount = invoices.filter(inv => !inv.confirmed).length;
        if (pendingCount === 0) {
            toast.info('No hay facturas pendientes para verificar');
            return;
        }

        setVerifyingAll(true);
        toast.info(`Verificando ${pendingCount} facturas pendientes...`);
        
        try {
            const response = await axios.post(
                `${API}/invoices/verify-all-pending`,
                {},
                { headers: getAuthHeaders() }
            );
            
            const { verified, not_found, message } = response.data;
            
            if (verified > 0) {
                toast.success(`✅ ${message}`);
                // Refresh invoices to show updated status
                fetchInvoices();
            } else {
                toast.warning(`⚠️ ${message}`);
            }
        } catch (error) {
            console.error('Error verifying all pending:', error);
            toast.error('Error al verificar facturas pendientes');
        } finally {
            setVerifyingAll(false);
        }
    };

    const handleDelete = async (invoiceId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta factura?')) return;
        
        const deleted = invoices.find(inv => inv.id === invoiceId);
        try {
            await axios.delete(`${API}/invoices/${invoiceId}`, {
                headers: getAuthHeaders()
            });
            setInvoices(invoices.filter(inv => inv.id !== invoiceId));
            toastDeletedWithUndo({
                invoiceId,
                label: deleted ? `${deleted.name} · ${formatCurrency(deleted.amount)}` : '',
                headers: getAuthHeaders(),
                onRestored: () => fetchInvoices(),
            });
        } catch (error) {
            console.error('Error deleting invoice:', error);
            toast.error('Error al eliminar la factura');
        }
    };

    const openManualVerifyModal = (invoice) => {
        setManualVerifyInvoice(invoice);
        setManualVerifyNote('');
        setManualVerifyBank('');
        setManualVerifySender(invoice.name);
        setManualVerifyModal(true);
    };

    const handleManualVerify = async () => {
        if (!manualVerifyInvoice) return;
        
        setConfirmingId(manualVerifyInvoice.id);
        try {
            const response = await axios.post(
                `${API}/invoices/${manualVerifyInvoice.id}/manual-verify`,
                {
                    bank: manualVerifyBank || 'Verificación Manual',
                    sender_name: manualVerifySender || manualVerifyInvoice.name,
                    note: manualVerifyNote
                },
                { headers: getAuthHeaders() }
            );
            
            setInvoices(invoices.map(inv => 
                inv.id === manualVerifyInvoice.id 
                    ? { ...inv, confirmed: true, auto_verified: true, verification_details: response.data.verification_details }
                    : inv
            ));
            
            toast.success(`✅ Factura verificada manualmente - ${manualVerifyBank || 'Manual'}`);
            setManualVerifyModal(false);
            setManualVerifyInvoice(null);
        } catch (error) {
            console.error('Error in manual verification:', error);
            toast.error('Error al verificar manualmente');
        } finally {
            setConfirmingId(null);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const handleAmountUpdate = async (invoiceId, newAmount) => {
        try {
            const res = await axios.patch(
                `${API}/invoices/${invoiceId}/amount`,
                { amount: newAmount },
                { headers: getAuthHeaders() }
            );
            const data = res.data;
            if (data.auto_verified) {
                setInvoices(invoices.map(inv =>
                    inv.id === invoiceId
                        ? { ...inv, amount: newAmount, amount_edited: true, confirmed: true, auto_verified: true, verification_details: data.verification_details }
                        : inv
                ));
                toast.success(`Monto actualizado a $${newAmount.toFixed(2)} — Factura verificada automáticamente`);
            } else {
                setInvoices(invoices.map(inv =>
                    inv.id === invoiceId
                        ? { ...inv, amount: newAmount, amount_edited: true }
                        : inv
                ));
                toast.success(`Monto actualizado a $${newAmount.toFixed(2)}`);
            }
        } catch (error) {
            console.error('Error updating amount:', error);
            toast.error('Error al actualizar el monto');
        }
    };

    const handleCategoryUpdate = async (invoiceId, newCategory) => {
        try {
            const res = await axios.put(
                `${API}/invoices/${invoiceId}/category`,
                { category: newCategory },
                { headers: getAuthHeaders() }
            );
            const data = res.data;
            setInvoices(invoices.map(inv =>
                inv.id === invoiceId
                    ? {
                        ...inv,
                        category: data.category,
                        amount: data.amount,
                        original_amount: data.original_amount,
                        stripe_fee_applied: data.stripe_fee_applied
                    }
                    : inv
            ));
            const feeNote = data.old_amount !== data.amount
                ? ` (monto: $${data.old_amount.toFixed(2)} → $${data.amount.toFixed(2)})`
                : '';
            toast.success(`Categoría cambiada a ${data.category}${feeNote}`);
        } catch (error) {
            console.error('Error updating category:', error);
            toast.error(error.response?.data?.detail || 'Error al actualizar la categoría');
        }
    };

    const handleDescriptionUpdate = async (invoiceId, newDescription) => {
        try {
            const res = await axios.patch(
                `${API}/invoices/${invoiceId}/description`,
                { description: newDescription },
                { headers: getAuthHeaders() }
            );
            setInvoices(invoices.map(inv => inv.id === invoiceId ? { ...inv, description: res.data.description } : inv));
            toast.success('Descripción actualizada');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar la descripción');
            throw error;
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                        Facturas
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {user?.role === 'usuario' 
                            ? 'Tus facturas registradas' 
                            : 'Todas las facturas del sistema'}
                    </p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    {/* Verify All Pending Button - Only for admin/supervisor */}
                    {(user?.role === 'admin' || user?.role === 'supervisor') && invoices.filter(i => !i.confirmed).length > 0 && (
                        <Button
                            onClick={handleVerifyAllPending}
                            disabled={verifyingAll}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            data-testid="verify-all-pending-btn"
                        >
                            {verifyingAll ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Verificando...
                                </>
                            ) : (
                                <>
                                    <Mail className="mr-2 h-4 w-4" />
                                    Verificar Pendientes ({invoices.filter(i => !i.confirmed).length})
                                </>
                            )}
                        </Button>
                    )}
                    {user?.role === 'admin' && (
                        <ExportButtons 
                            data={sortedInvoices}
                            filename="facturas"
                            title="Reporte de Facturas"
                            subtitle={`Filtros: ${dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Sin fecha inicio'} - ${dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Sin fecha fin'}${selectedUserId && selectedUserId !== 'all' ? ` | Usuario: ${users.find(u => u.id === selectedUserId)?.name || 'Seleccionado'}` : ''}`}
                        />
                    )}
                    <span className="badge badge-success" data-testid="header-confirmed-badge">
                        {summary ? summary.confirmed_count : invoices.filter(i => i.confirmed).length} Confirmadas
                    </span>
                    <span className="badge badge-pending" data-testid="header-pending-badge">
                        {summary ? summary.pending_count : invoices.filter(i => !i.confirmed).length} Pendientes
                    </span>
                </div>
            </div>

            {/* Filters Section */}
            <InvoicesFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
                users={users}
                canFilterByUser={canFilterByUser}
                hasActiveFilters={hasActiveFilters}
                onSearch={handleSearch}
                onClear={clearFilters}
                onSelectDay={selectSingleDay}
                onSelectRange={selectRange}
            />

            {/* Invoices Table */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                    {invoices.length > 0 ? (
                        <>
                            <InvoicesTable
                                invoices={paginatedInvoices}
                                userRole={user?.role}
                                userId={user?.id}
                                sortKey={sortKey}
                                sortDirection={sortDirection}
                                confirmingId={confirmingId}
                                getAuthHeaders={getAuthHeaders}
                                onSort={handleSort}
                                onViewInvoice={setSelectedInvoice}
                                onConfirm={handleConfirm}
                                onVerifyZelle={handleVerifyZelle}
                                onReVerify={handleReVerify}
                                onManualVerify={openManualVerifyModal}
                                onDelete={handleDelete}
                                onAmountUpdate={handleAmountUpdate}
                                onCategoryUpdate={handleCategoryUpdate}
                                formatCurrency={formatCurrency}
                                formatDate={formatDate}
                                onRefresh={() => fetchInvoices()}
                            />
                            
                            {/* Load More Button */}
                            {hasMoreInvoices && (
                                <div className="mt-4 p-4 text-left border-t border-slate-200">
                                    <Button
                                        variant="outline"
                                        onClick={loadMore}
                                        className="text-sky-600 border-sky-200 hover:bg-sky-50"
                                        data-testid="load-more-invoices"
                                    >
                                        Cargar más ({Math.min(visibleCount, sortedInvoices.length)} de {totalCount}){loadingMore ? '…' : ''}
                                    </Button>
                                </div>
                            )}
                            
                            {/* Summary */}
                            <InvoicesSummary 
                                invoices={sortedInvoices}
                                formatCurrency={formatCurrency}
                                showAdminFee={user?.role === 'admin'}
                                summary={summary}
                            />
                        </>
                    ) : (
                        <div className="empty-state py-16">
                            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-lg font-medium text-slate-600">No hay facturas</p>
                            <p className="text-slate-400 mt-1">
                                {user?.role === 'usuario' 
                                    ? 'Sube tu primera factura desde el menú' 
                                    : 'Los usuarios aún no han subido facturas'}
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Invoice Detail Modal */}
            <InvoiceDetailModal
                invoice={selectedInvoice && (invoices.find(inv => inv.id === selectedInvoice.id) || selectedInvoice)}
                onClose={() => setSelectedInvoice(null)}
                formatCurrency={formatCurrency}
                isAdmin={user?.role === 'admin'}
                onAmountUpdate={handleAmountUpdate}
                onCategoryUpdate={handleCategoryUpdate}
                onDescriptionUpdate={handleDescriptionUpdate}
                onReassign={(inv) => setReassign({ invoiceId: inv.id, invoiceName: inv.name, amount: inv.amount, currentUserName: inv.user_name })}
            />
            <ReassignUserDialog
                target={reassign}
                onClose={() => setReassign(null)}
                onDone={(r) => {
                    // Extra users' invoices leave the regular list; regular ones just change owner
                    setInvoices(invoices.map(inv => inv.id === reassign?.invoiceId ? { ...inv, user_id: r.user_id, user_name: r.user_name, is_extra: r.is_extra } : inv).filter(inv => !inv.is_extra));
                    if (r.is_extra) setSelectedInvoice(null);
                }}
            />

            {/* Manual Verification Modal */}
            <ManualVerifyModal
                isOpen={manualVerifyModal}
                invoice={manualVerifyInvoice}
                bank={manualVerifyBank}
                setBank={setManualVerifyBank}
                sender={manualVerifySender}
                setSender={setManualVerifySender}
                note={manualVerifyNote}
                setNote={setManualVerifyNote}
                isLoading={confirmingId === manualVerifyInvoice?.id}
                onClose={() => setManualVerifyModal(false)}
                onVerify={handleManualVerify}
                formatCurrency={formatCurrency}
            />
        </div>
    );
}
